import React, { useCallback, useEffect, useMemo, useState } from "react";
import { InitialView } from "./InitialView";
import { ExpandedView } from "./ExpandedView";
import { useFetchFeature } from "../common/utils/features";
import { sharedEventEmitter } from "../common/utils/sharedEvents";
import {
    getCookie,
    setCookie,
    parseConsentCookieString,
    CONSENT_COOKIE_NAME,
    deleteOldConsentCookie
} from "./cookieUtils";

/**
 * Serializes the current consent state into a cookie string format.
 * @param currentConsents The current consent state object.
 * @param allServiceIds A list of all known service IDs to ensure all are included in the cookie.
 * @returns A string representation of the consent state for the cookie.
 */
const serializeConsentStateToCookieString = (
    currentConsents: Record<string, boolean | undefined>,
    allServiceIds: string[]
): string => {
    let cookieString = "";

    allServiceIds.forEach(id => {
        let consentValue = "wait";
        if (currentConsents[id] === true) {
            consentValue = "true";
        } else if (currentConsents[id] === false) {
            consentValue = "false";
        }
        cookieString += `!${id}=${consentValue}`;
    });
    return cookieString;
};
// --- End Cookie Utility Functions ---

/** All UI text labels used in the consent modal, sourced from feature settings. */
export interface ConsentStaticTexts {
    "main-title": string;
    "main-description": string;
    "reference-link": string;
    "personalize-button": string;
    "personalize-title": string;
    "accept-all-button": string;
    "accept-essential-button": string;
    "personalize-accept-essential-button": string;
    "save-choices-button": string;
    "control-all-services-button": string;
    "show-more-button": string;
}

/** A logical grouping of consent services (e.g. Analytics, Marketing). */
export interface ConsentCategory {
    title: string;
    description: string;
    code: string;
}

/** A single third-party service that requires user consent to be activated. */
export interface ConsentService {
    code: string;
    title: string;
    description: string;
    link: string;
    category: string;
}

/**
 * Cookie consent modal that manages GDPR/cookie consent for the portal.
 *
 * On mount it reads the existing consent cookie and shows the modal if any
 * service consent is still pending. It also listens for a global
 * "open-consent-modal" event so other parts of the app can reopen it
 * programmatically (e.g. from a privacy-settings link).
 *
 * Renders either the compact `InitialView` or the full `ExpandedView`
 * depending on whether the user has clicked "Personalize".
 */
export const CookieConsentModal = () => {
    // Controls whether the modal is currently visible.
    const [isOpen, setIsOpen] = useState(false);
    // Controls whether the detailed per-service view is shown instead of the summary view.
    const [isExpanded, setIsExpanded] = useState(false);
    const { enabled, settings } = useFetchFeature("consent");
    // UI label strings loaded from feature settings.
    const [texts, setTexts] = useState({} as ConsentStaticTexts);
    // Per-service consent state; `undefined` means the user has not yet decided.
    const [serviceConsents, setServiceConsents] = useState<Record<string, boolean | undefined>>({});
    // Flat list of service identifiers derived from feature settings.
    const CONSENT_SERVICES = useMemo(() => getConsentServicesFromSettings(settings), [settings]);
    const CONSENT_SERVICE_IDS = useMemo(() => CONSENT_SERVICES.map(service => service.code), [CONSENT_SERVICES]);
    const CONSENT_CATEGORIES = useMemo(() => getConsentServiceCategoriesFromSettings(settings), [settings]);
    /** Opens the modal, optionally jumping straight to the expanded/personalize view. */
    const openConsentModal = useCallback((expanded = false) => {
        setIsExpanded(expanded);
        setIsOpen(true);
    }, []);
    useEffect(() => {
        if (enabled) {
            //deleting deprecated cookie if it exists
            deleteOldConsentCookie();
            setTexts(settings.texts as ConsentStaticTexts);
            // Initialize consents from browser cookie
            const consentCookie = getCookie(CONSENT_COOKIE_NAME);
            const parsedConsents = parseConsentCookieString(consentCookie || "");
            const initialConsents: Record<string, boolean | undefined> = {};
            CONSENT_SERVICE_IDS.forEach(id => {
                if (parsedConsents[id] === "true") {
                    initialConsents[id] = true;
                } else {
                    initialConsents[id] = false;
                }
            });
            setServiceConsents(initialConsents);
            const isConsentPending = CONSENT_SERVICE_IDS.some(
                id => !parsedConsents[id] || parsedConsents[id] === "wait"
            );
            setIsOpen(isConsentPending);
            // Listen for external requests to open the modal
            const handleOpenRequest = (data: { expanded?: boolean }) => {
                openConsentModal(data?.expanded);
            };
            sharedEventEmitter.on("open-consent-modal", handleOpenRequest);
            return () => {
                sharedEventEmitter.off("open-consent-modal", handleOpenRequest);
            };
        }
    }, [enabled, settings, CONSENT_SERVICE_IDS, openConsentModal]);

    /** Updates the in-memory consent toggle for a single service without persisting yet. */
    const handleConsentChange = (serviceId: string, isEnabled: boolean) => {
        setServiceConsents(prevConsents => ({
            ...prevConsents,
            [serviceId]: isEnabled
        }));
    };

    /**
     * Saves the current consent state to a cookie and collapses the modal.
     * @param consentsToSave The consent state to be saved.
     * Also triggers a server update to save the consents if an update URL is provided in settings.
     */
    const saveConsentsAndCollapse = async (consentsToSave: Record<string, boolean | undefined>) => {
        // Retrieve the previous consent state from the cookie to detect changes.
        const oldCookie = getCookie(CONSENT_COOKIE_NAME);
        const oldConsents = parseConsentCookieString(oldCookie || "");
        let consentRevoked = false;

        // Check if any service consent has been revoked (changed from true to false).
        for (const id of CONSENT_SERVICE_IDS) {
            const oldConsent = oldConsents[id] === "true";
            const newConsent = consentsToSave[id] === true;
            // If a previously granted consent is now being denied, flag it.
            if (oldConsent && !newConsent) {
                consentRevoked = true;
                break;
            }
        }

        // Finalize the consent state. Any service with an 'undefined' state is treated as not consented (false).
        const finalConsents: Record<string, boolean> = {};
        // When saving, any service still in 'undefined' state is considered as not consented (false).
        CONSENT_SERVICE_IDS.forEach(id => {
            finalConsents[id] = consentsToSave[id] === true;
        });

        // Notify other parts of the application that the consent has changed.
        sharedEventEmitter.emit("consent-changed", finalConsents);
        // Serialize the final consent state and save it to a cookie for 1 year.
        const cookieValue = serializeConsentStateToCookieString(finalConsents, CONSENT_SERVICE_IDS);
        setCookie(CONSENT_COOKIE_NAME, cookieValue, 365); // Store for 1 year
        setServiceConsents(finalConsents);
        if (settings && settings["update-url"]) {
            try {
                await fetch(settings["update-url"], {
                    method: "POST",
                    mode: "no-cors",
                    credentials: "include"
                });
            } catch (error) {
                console.error("Failed to update consent on server:", error);
            }
        }
        // Close the consent modal UI.
        setIsOpen(false);
        // If consent for any service was revoked, reload the page to ensure all services are correctly disabled.
        if (consentRevoked) {
            window.location.reload();
        }
    };
    /** Denies all optional services and saves — only essential cookies are kept. */
    const handleAcceptEssential = async () => {
        const newConsents: Record<string, boolean> = {};
        CONSENT_SERVICE_IDS.forEach(id => {
            newConsents[id] = false;
        });
        setServiceConsents(newConsents);
        await saveConsentsAndCollapse(newConsents);
    };
    /** Persists whatever per-service toggles the user has set in the expanded view. */
    const handleSaveChoices = async () => {
        await saveConsentsAndCollapse(serviceConsents);
    };
    /** Grants consent for every service and saves. */
    const handleAcceptAll = async () => {
        const newConsents: Record<string, boolean> = {};
        CONSENT_SERVICE_IDS.forEach(id => {
            newConsents[id] = true;
        });
        setServiceConsents(newConsents);
        await saveConsentsAndCollapse(newConsents);
    };
    if (!enabled || !isOpen) {
        return null;
    }
    // ExpandedView requires strict boolean values; coerce any remaining `undefined` to false.
    const serviceConsentsForExpandedView: Record<string, boolean> = Object.fromEntries(
        Object.entries(serviceConsents).map(([id, value]) => [id, value === true])
    );

    return isExpanded ? (
        <ExpandedView
            onCollapse={() => openConsentModal(false)}
            texts={texts}
            consents={CONSENT_SERVICES}
            categories={CONSENT_CATEGORIES}
            serviceConsents={serviceConsentsForExpandedView}
            onConsentChange={handleConsentChange}
            onAcceptEssential={handleAcceptEssential}
            onSaveChoices={handleSaveChoices}
            onAcceptAll={handleAcceptAll} // Pass this to ExpandedView
        />
    ) : (
        <InitialView
            onExpand={() => openConsentModal(true)}
            texts={texts}
            onAcceptAll={handleAcceptAll}
            onAcceptEssential={handleAcceptEssential}
        />
    );
};

function getConsentServicesFromSettings(settings: any): ConsentService[] {
    return settings?.["consents"] || [];
}

function getConsentServiceCategoriesFromSettings(settings: any): ConsentCategory[] {
    return settings?.["consent-categories"] || [];
}
